const state = {
    metrics: [],
    maxMetrics: 50, // Keep last 50 metrics
    thresholds: {
        queryCount: {
            warning: 10,
            critical: 50,
        },
        queryTime: {
            warning: 100, // ms
            critical: 500, // ms
        },
        requestTime: {
            warning: 200, // ms
            critical: 1000, // ms
        },
        queryComplexity: {
            warning: 30, // complexity score
            critical: 70, // complexity score
        },
    },
};

const mutations = {
    ADD_METRIC(state, metric) {
        state.metrics.unshift(metric);
        // Keep only the last maxMetrics
        if (state.metrics.length > state.maxMetrics) {
            state.metrics = state.metrics.slice(0, state.maxMetrics);
        }
    },
    CLEAR_METRICS(state) {
        state.metrics = [];
    },
    SET_THRESHOLDS(state, thresholds) {
        state.thresholds = { ...state.thresholds, ...thresholds };
    },
};

const actions = {
    addMetric({ commit, state }, metric) {
        commit('ADD_METRIC', metric);
        
        // Log slow requests to console
        if (metric.queryCount && metric.queryCount > state.thresholds.queryCount.critical) {
            console.warn(`[Performance] High query count: ${metric.queryCount} queries for ${metric.method} ${metric.url}`);
        }
        if (metric.queryTime && metric.queryTime > state.thresholds.queryTime.critical) {
            console.warn(`[Performance] Slow SQL queries: ${metric.queryTime.toFixed(2)}ms for ${metric.method} ${metric.url}`);
        }
        if (metric.requestTime && metric.requestTime > state.thresholds.requestTime.critical) {
            console.warn(`[Performance] Slow request: ${metric.requestTime.toFixed(2)}ms for ${metric.method} ${metric.url}`);
        }
        if (metric.queryComplexity && metric.queryComplexity > state.thresholds.queryComplexity.critical) {
            console.warn(`[Performance] High query complexity: ${metric.queryComplexity.toFixed(2)} for ${metric.method} ${metric.url}`);
        }
    },
    clearMetrics({ commit }) {
        commit('CLEAR_METRICS');
    },
    setThresholds({ commit }, thresholds) {
        commit('SET_THRESHOLDS', thresholds);
    },
};

const getters = {
    metrics: (state) => state.metrics,
    latestMetric: (state) => state.metrics[0] || null,
    averageQueryCount: (state) => {
        if (state.metrics.length === 0) return 0;
        const total = state.metrics.reduce((sum, m) => sum + (m.queryCount || 0), 0);
        return Math.round(total / state.metrics.length);
    },
    averageQueryTime: (state) => {
        if (state.metrics.length === 0) return 0;
        const metricsWithTime = state.metrics.filter(m => m.queryTime !== null);
        if (metricsWithTime.length === 0) return 0;
        const total = metricsWithTime.reduce((sum, m) => sum + (m.queryTime || 0), 0);
        return Math.round(total / metricsWithTime.length);
    },
    averageRequestTime: (state) => {
        if (state.metrics.length === 0) return 0;
        const metricsWithTime = state.metrics.filter(m => m.requestTime !== null);
        if (metricsWithTime.length === 0) return 0;
        const total = metricsWithTime.reduce((sum, m) => sum + (m.requestTime || 0), 0);
        return Math.round(total / metricsWithTime.length);
    },
    maxQueryCount: (state) => {
        if (state.metrics.length === 0) return 0;
        return Math.max(...state.metrics.map(m => m.queryCount || 0));
    },
    maxQueryTime: (state) => {
        if (state.metrics.length === 0) return 0;
        const times = state.metrics.map(m => m.queryTime || 0).filter(t => t > 0);
        return times.length > 0 ? Math.max(...times) : 0;
    },
    maxRequestTime: (state) => {
        if (state.metrics.length === 0) return 0;
        const times = state.metrics.map(m => m.requestTime || 0).filter(t => t > 0);
        return times.length > 0 ? Math.max(...times) : 0;
    },
    getMetricStatus: (state) => (metric) => {
        if (!metric) return 'good';
        
        const { queryCount, queryTime, requestTime, queryComplexity } = metric;
        const { thresholds } = state;
        
        // Check for critical status
        if (
            (queryCount && queryCount >= thresholds.queryCount.critical) ||
            (queryTime && queryTime >= thresholds.queryTime.critical) ||
            (requestTime && requestTime >= thresholds.requestTime.critical) ||
            (queryComplexity && queryComplexity >= thresholds.queryComplexity.critical)
        ) {
            return 'critical';
        }
        
        // Check for warning status
        if (
            (queryCount && queryCount >= thresholds.queryCount.warning) ||
            (queryTime && queryTime >= thresholds.queryTime.warning) ||
            (requestTime && requestTime >= thresholds.requestTime.warning) ||
            (queryComplexity && queryComplexity >= thresholds.queryComplexity.warning)
        ) {
            return 'warning';
        }
        
        return 'good';
    },
    thresholds: (state) => state.thresholds,
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};

